import React from "react";
import { useTranslation } from "react-i18next";
import { EFactEAttestEnum, translateKey } from "common/enums/EFactEAttestEnum";
import { GlobalState } from "store/configure-store";
import {
  selectCt1,
  selectCt2,
  selectEFactEAttest,
} from "store/reducers/patient.reducer";
import { connect } from "react-redux";
import { setEFactEAttest } from "store/actions/patient.actions";
import Card from "@cc/react-components/Card";
import ListGroup from "ui/ListGroup";
import Page from "ui/Page";
import { selectEAttestAvailable } from "store/reducers/user-context.reducer";
import Alert from "@cc/react-components/Alert";
import { PV_EFACT_EATTEST } from "common/matomo/track-page-view.constants";
import { useTrackPageView } from "common/matomo/track-page-view.hook";

interface StateFromProps {
  ct1: string;
  ct2: string;
  eFactAttest: EFactEAttestEnum;
  eAttestAvailable: boolean;
}

interface DispatchFromProps {
  setEFactEAttest(eFacteAttestEnum: EFactEAttestEnum): void;
}

const EFactEAttestContainer = ({
  ct1,
  ct2,
  eFactAttest,
  eAttestAvailable,
  setEFactEAttest,
}: StateFromProps & DispatchFromProps) => {
  const [t] = useTranslation();
  const hasInsurabilityInformation = !!ct1 || !!ct2;
  const subTitle = !hasInsurabilityInformation
    ? t("eFacteAttest.noInfo")
    : t("eFacteAttest.subtitle") + " " + ct1 + "/" + ct2;

  if (!hasInsurabilityInformation && eFactAttest !== EFactEAttestEnum.EAttest) {
    setEFactEAttest(EFactEAttestEnum.EAttest);
  }

  useTrackPageView(PV_EFACT_EATTEST);

  const renderResults = () => {
    return (
      <ListGroup>
        <ListGroup.ItemMobile
          key={EFactEAttestEnum.EFact}
          disabled={!hasInsurabilityInformation}
          active={eFactAttest === EFactEAttestEnum.EFact}
          onClick={() => setEFactEAttest(EFactEAttestEnum.EFact)}
        >
          {t(translateKey(EFactEAttestEnum[EFactEAttestEnum.EFact]))}
        </ListGroup.ItemMobile>
        <ListGroup.ItemMobile
          key={EFactEAttestEnum.EAttest}
          disabled={!eAttestAvailable}
          active={eAttestAvailable && eFactAttest === EFactEAttestEnum.EAttest}
          onClick={() => setEFactEAttest(EFactEAttestEnum.EAttest)}
        >
          {t(translateKey(EFactEAttestEnum[EFactEAttestEnum.EAttest]))}
        </ListGroup.ItemMobile>
      </ListGroup>
    );
  };

  return (
    <Page.Scrollable>
      <Page.Body>
        <section>
          <Card>
            <Card.Header>
              <Card.Title>{t("eFacteAttest.title")}:</Card.Title>
            </Card.Header>
            <Card.Body>{subTitle}</Card.Body>
          </Card>
        </section>
        <section>{renderResults()}</section>
        {!eAttestAvailable && !hasInsurabilityInformation && (
          <section>
            <Alert variant="warning">
              {t("eFacteAttest.tarificationNotPossible")}
            </Alert>
          </section>
        )}
      </Page.Body>
    </Page.Scrollable>
  );
};

const mapStateToProps = (state: GlobalState) => {
  return {
    ct1: selectCt1(state),
    ct2: selectCt2(state),
    eFactAttest: selectEFactEAttest(state),
    eAttestAvailable: selectEAttestAvailable(state),
  };
};

export default connect(mapStateToProps, { setEFactEAttest })(
  EFactEAttestContainer
);
